import { AUTO_DIRECTOR_CAMERA, ClipStatus } from '@/features/clips/types';
import type { DownloadButtonState } from '../components/CameraDownloadIcon';

/**
 * Pure state logic behind the clip download buttons and the status line under
 * them (Figma Game View, section "Clip download states", node 4348:16338):
 *
 *   gray arrow        default, nothing exported for this camera yet
 *   blue spinner      generating
 *   green check       READY for the first 5 s, confirming the export landed
 *   green arrow       READY afterwards; click downloads instantly
 *   red X             ERROR for 3 s, then back to the gray arrow (click retries)
 *
 * The message line mirrors the most recent transition. Everything here is
 * synchronous and testable; `useClipDownloadFeedback` owns the timers.
 */

/** How long the green check (and its message) stay up once a clip turns READY. */
export const READY_FLASH_MS = 5000;
/** How long the red X stays up after an export fails before the arrow returns. */
export const ERROR_FLASH_MS = 3000;

export type FeedbackKind = 'generating' | 'ready' | 'error';
export type FlashKind = 'ready' | 'error';

export interface FeedbackClip {
  id?: number | string;
  cameraId?: number | null;
  autoDirector?: boolean;
  status?: string;
}

/** A group holds at most one clip per camera plus one Auto-Director clip; this names that slot. */
export const clipKey = (clip: Pick<FeedbackClip, 'cameraId' | 'autoDirector'>): string =>
  clip.autoDirector ? AUTO_DIRECTOR_CAMERA : String(clip.cameraId);

/** Slot key → upper-cased clip status. Slots without a status are absent. */
export type StatusMap = Record<string, string>;

export const normalizeStatus = (status?: string | null): string | undefined =>
  status ? status.toUpperCase() : undefined;

const GENERATING_STATUSES: ReadonlySet<string> = new Set([
  ClipStatus.INITIALIZING,
  ClipStatus.PENDING,
  ClipStatus.PROCESSING,
  ClipStatus.CANCELING,
]);

export const isGeneratingStatus = (status?: string): boolean =>
  !!status && GENERATING_STATUSES.has(status);

/** First clip per slot wins, matching the `clips.find` the buttons act on. */
export const toStatusMap = (clips: readonly FeedbackClip[]): StatusMap => {
  const map: StatusMap = {};
  for (const clip of clips) {
    const key = clipKey(clip);
    const status = normalizeStatus(clip.status);
    if (status && !(key in map)) map[key] = status;
  }
  return map;
};

export interface StatusTransition {
  key: string;
  /** `cleared`: the slot was removed, reset, canceled, or first seen already finished. */
  kind: FeedbackKind | 'cleared';
}

/**
 * The events the UI reacts to between two snapshots of a group's clips.
 *
 * READY and ERROR only count when the export was watched running, so a clip
 * that is already finished when the group first renders, or one the user
 * cancelled, neither flashes nor reports.
 */
export function diffStatusMaps(prev: StatusMap, next: StatusMap): StatusTransition[] {
  const transitions: StatusTransition[] = [];
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);

  for (const key of keys) {
    const before = prev[key];
    const after = next[key];
    if (before === after) continue;

    if (isGeneratingStatus(after)) {
      // PENDING → PROCESSING is still the same "generating" story.
      if (!isGeneratingStatus(before)) transitions.push({ key, kind: 'generating' });
      continue;
    }
    const watched = before !== undefined && before !== ClipStatus.CANCELING;
    if (after === ClipStatus.READY && watched) {
      transitions.push({ key, kind: 'ready' });
      continue;
    }
    if (after === ClipStatus.ERROR && watched) {
      transitions.push({ key, kind: 'error' });
      continue;
    }
    transitions.push({ key, kind: 'cleared' });
  }

  return transitions;
}

/** Icon for one slot, given its status and any transient flash still showing. */
export function resolveButtonState(status?: string, flash?: FlashKind): DownloadButtonState {
  if (!status) return 'default';
  if (flash) return flash;
  if (isGeneratingStatus(status)) return 'generating';
  if (status === ClipStatus.READY) return 'download';
  // NEW, ERROR after its flash, CANCELED: a plain arrow again. Clicking it
  // creates or retries the export.
  return 'default';
}

export interface FeedbackMessage {
  kind: FeedbackKind;
  key: string;
}

/** What to show once a transient message expires: the next clip still generating, or nothing. */
export const generatingMessage = (statuses: StatusMap): FeedbackMessage | null => {
  const key = Object.keys(statuses).find((k) => isGeneratingStatus(statuses[k]));
  return key ? { kind: 'generating', key } : null;
};

export const feedbackMessageText = (kind: FeedbackKind, cameraName: string): string => {
  switch (kind) {
    case 'generating':
      return `Generating ${cameraName} clip...`;
    case 'ready':
      return `${cameraName} clip created and download has begun.`;
    case 'error':
      return 'Clip generation failed. Please try again.';
  }
};

// Exports this browser session asked for. The clip-group query is shared by
// everyone looking at the event, so a READY seen on poll must only claim
// credit in the tab that clicked the camera.
const requestedExports = new Set<string>();
const requestId = (groupId: string | number, slot: string) => `${groupId}:${slot}`;

export const markExportRequested = (groupId: string | number, slot: string): void => {
  requestedExports.add(requestId(groupId, slot));
};

/** True once per request: was this slot's export started here? Clears the mark. */
export const consumeExportRequest = (groupId: string | number, slot: string): boolean =>
  requestedExports.delete(requestId(groupId, slot));
